import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { staffAPI, configAPI } from '../services/api';
import toast from '../utils/toast';

// Departments (cached longer since they rarely change)
export const useDepartments = () => {
  return useQuery({
    queryKey: ['config', 'departments'],
    queryFn: async () => {
      const response = await staffAPI.getDepartments();
      return Array.isArray(response.data) ? response.data : (response.data?.results || []);
    },
    staleTime: 30 * 60 * 1000, // 30 minutes - departments rarely change
  });
};

// Projects/Work sites
export const useProjects = (options = {}) => {
  const { page_size = 1000, status, ...queryOptions } = options;
  
  return useQuery({
    queryKey: ['config', 'projects', { page_size, status }],
    queryFn: async () => {
      const params = { page_size };
      if (status) params.status = status;
      
      const response = await staffAPI.getProjects(params);
      return response.data?.results || response.data || [];
    },
    staleTime: 15 * 60 * 1000, // 15 minutes
    ...queryOptions,
  });
};

// Work sites
export const useWorkSites = () => {
  return useQuery({
    queryKey: ['config', 'work-sites'],
    queryFn: async () => {
      const response = await staffAPI.getWorkSites();
      return Array.isArray(response.data) ? response.data : (response.data?.results || []);
    },
    staleTime: 15 * 60 * 1000, // 15 minutes
  });
};

// Trade categories
export const useTradeCategories = () => {
  return useQuery({
    queryKey: ['config', 'trade-categories'],
    queryFn: async () => {
      const response = await configAPI.getTradeCategories();
      return response.data?.results || response.data || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
};

// MOM designation titles (filtered by pass type for Add Employee).
// WP / S Pass return the curated MOM list; EP returns values learned over time
// from free-text entries.
export const useMOMDesignations = (passType, options = {}) => {
  const applies = passType === 'wp' || passType === 's_pass' || passType === 'ep';
  return useQuery({
    queryKey: ['config', 'mom-designations', passType],
    queryFn: async () => {
      const response = await configAPI.getMOMDesignations({ pass_type: passType });
      return response.data?.results || response.data || [];
    },
    staleTime: 30 * 60 * 1000,
    enabled: applies && (options.enabled !== false),
    ...options,
  });
};

// Distinct designations in active use (for combobox suggestions)
export const useDistinctDesignations = () => {
  return useQuery({
    queryKey: ['config', 'distinct-designations'],
    queryFn: async () => {
      const response = await staffAPI.getDistinctDesignations();
      return response.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

// Designations (reference table)
export const useDesignations = () => {
  return useQuery({
    queryKey: ['config', 'designations'],
    queryFn: async () => {
      const response = await configAPI.getDesignations();
      return response.data?.results || response.data || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Employment pass types
export const useEmploymentPassTypes = () => {
  return useQuery({
    queryKey: ['config', 'employment-pass-types'],
    queryFn: async () => {
      const response = await configAPI.getEmploymentPassTypes();
      return response.data?.results || response.data || [];
    },
    staleTime: 60 * 60 * 1000, // 1 hour - rarely changes
  });
};

// Allowance types
export const useAllowanceTypes = () => {
  return useQuery({
    queryKey: ['config', 'allowance-types'],
    queryFn: async () => {
      // AllowanceTypes moved to configuration app
      const response = await staffAPI.getAllowanceTypes();
      return response.data?.results || response.data || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Leave policies
export const useLeavePolicies = () => {
  return useQuery({
    queryKey: ['config', 'leave-policies'],
    queryFn: async () => {
      const response = await configAPI.getLeavePolicies();
      return response.data?.results || response.data || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Configuration mutations
export const useUpdateDepartment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => configAPI.updateDepartment(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config', 'departments'] });
      // Also invalidate staff list since departments affect staff data
      queryClient.invalidateQueries({ queryKey: ['staff', 'list'] });
      toast.success('Department updated successfully');
    },
    onError: (error) => {
      console.error('Error updating department:', error);
      toast.error('Failed to update department');
    },
  });
};

export const useCreateDepartment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data) => configAPI.createDepartment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config', 'departments'] });
      toast.success('Department created successfully');
    },
    onError: (error) => {
      console.error('Error creating department:', error);
      toast.error('Failed to create department');
    },
  });
};

export const useUpdateProject = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => staffAPI.updateProject(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config', 'projects'] });
      queryClient.invalidateQueries({ queryKey: ['config', 'work-sites'] });
      toast.success('Project updated successfully');
    },
    onError: (error) => {
      console.error('Error updating project:', error);
      toast.error('Failed to update project');
    },
  });
};

/** Static Singapore bank catalog (legal name, SWIFT BIC, MAS bank code). */
export const useSingaporeBanks = (options = {}) => {
  return useQuery({
    queryKey: ['config', 'singaporeBanks'],
    queryFn: async () => {
      const response = await configAPI.getSingaporeBanks();
      return Array.isArray(response.data) ? response.data : (response.data?.results || []);
    },
    staleTime: 60 * 60 * 1000, // 1 hour — reference data
    ...options,
  });
};
